import { styled } from 'styled-components'

import { getName } from 'peach/helpers'
import { useParams } from 'peach/router'

import {
  FormContainer,
  FormSection,
  MultiSelectWithLabel,
  SelectWithLabel,
  TextInputWithLabel,
  Clickable,
  SubmitContainer,
} from 'core/components'
import { R } from 'core/helpers'
import { useForm } from 'core/hooks'
import {
  useCompanyRoleGetAll,
  useInternalEmployeesGet,
  useInternalEmployeeCreate,
  useInternalUserCreate,
  useInternalEmployeeAddToTeam,
  useInternalTeamsGet,
} from 'core/types'

type FormValues = {
  authType: string | null
  email: string | null
  password: string | null
  samlSubjectNameId: string | null
  roles: Array<string>
  firstName: string
  lastName: string
  jobName: string
  managerEmployeeId: string
  teamId: string
}

type AddEmployeeFormProps = {
  onSuccess?: () => void
}

const AddEmployeeForm = ({ onSuccess }: AddEmployeeFormProps) => {
  const { companyId } = useParams<{ companyId: string }>()

  const { data: roles } = useCompanyRoleGetAll({
    companyId,
    query: { limit: 100 },
  })

  const roleOptions = R.map(roles ?? [], (role) => ({
    value: role.id,
    label: getName(role),
  }))

  const { data: employees = [], isLoading: isLoadingEmployees } =
    useInternalEmployeesGet({
      companyId,
      query: { limit: 100 },
    })

  const employeeOptions = R.map(employees ?? [], (employee) => ({
    value: employee.id,
    label: getName(employee),
  }))

  const { data: teams = [], isLoading: isLoadingTeams } = useInternalTeamsGet({
    companyId,
  })

  const teamOptions = R.map(teams ?? [], (team) => {
    const { id, name } = team

    return {
      value: id,
      label: name ? `${name} (${id})` : id,
    }
  })

  const { mutateAsync: addCompanyEmployee } = useInternalEmployeeCreate({
    companyId,
  })

  const { mutateAsync: addCompanyUser } = useInternalUserCreate({ companyId })

  const { mutateAsync: addEmployeeToTeam } = useInternalEmployeeAddToTeam({
    companyId,
    employeeId: undefined,
    teamId: undefined,
  })

  const { fields, formState, handleSubmit } = useForm<FormValues>(
    {
      authType: 'oneTimeCodeEmail',
      email: '',
      password: '',
      samlSubjectNameId: '',
      roles: [],
      firstName: '',
      lastName: '',
      jobName: '',
      managerEmployeeId: '',
      teamId: '',
    },
    {
      onSubmit: async ({
        // user options
        email,
        password,
        samlSubjectNameId,
        roles,
        // employee options
        firstName,
        lastName,
        jobName,
        managerEmployeeId,

        // team options

        teamId,
      }) => {
        const userResp = await addCompanyUser({
          type: 'agent',
          authType: {
            email: email || undefined,
            password: password || undefined,
            samlSubjectNameId: samlSubjectNameId || undefined,
          },
          roleIds: roles || [],
        })

        const employeeResp = await addCompanyEmployee({
          firstName,
          lastName,
          jobName: jobName || undefined,
          userId: userResp?.data?.id,
          managerEmployeeId: managerEmployeeId || undefined,
          email: email || undefined,
        })

        if (teamId) {
          await addEmployeeToTeam(undefined, {
            employeeId: employeeResp?.data?.id,
            teamId,
          })
        }

        // Call onSuccess callback after successful submission
        onSuccess?.()
      },
      rules: {
        email: (value, { values }) =>
          (values.authType === 'oneTimeCodeEmail' ||
            values.authType === 'emailPassword') &&
          !value
            ? 'Email is required'
            : true,
        password: (value, { values }) =>
          values.authType === 'emailPassword' && !value
            ? 'Password is required'
            : true,
        samlSubjectNameId: (value, { values }) =>
          values.authType === 'SAML' && !value
            ? 'SAML Subject Name ID is required'
            : true,
        firstName: (value) => (!value ? 'First name is required' : true),
        lastName: (value) => (!value ? 'Last name is required' : true),
      },
      validationType: 'onChange',
    },
  )

  return (
    <form onSubmit={handleSubmit}>
      <FormContainer
        isLoading={isLoadingEmployees || isLoadingTeams}
        isSubmitting={formState.isSubmitting}
        title='Add Employee'
      >
        <FormSection label='User Record Properties'>
          <StyledSelectWithLabel
            label='Auth type'
            options={[
              { label: 'One time code email', value: 'oneTimeCodeEmail' },
              { label: 'Basic', value: 'emailPassword' },
              { label: 'SAML', value: 'SAML' },
            ]}
            {...fields.authType}
          />
          {(fields.authType.value === 'emailPassword' ||
            fields.authType.value === 'oneTimeCodeEmail') && (
            <StyledTextInputWithLabel
              label='Email'
              variant='outline'
              {...fields.email}
            />
          )}
          {fields.authType.value === 'emailPassword' && (
            <StyledTextInputWithLabel
              label='Password'
              type='password'
              variant='outline'
              {...fields.password}
            />
          )}
          {fields.authType.value === 'SAML' && (
            <StyledTextInputWithLabel
              label='SAML Subject Name ID'
              variant='outline'
              {...fields.samlSubjectNameId}
            />
          )}

          <StyledMultiSelectWithLabel
            label='Roles'
            options={roleOptions}
            wrap
            {...fields.roles}
          />
        </FormSection>

        <FormSection label='Employee Record Properties'>
          <StyledTextInputWithLabel
            label='First Name'
            variant='outline'
            {...fields.firstName}
          />
          <StyledTextInputWithLabel
            label='Last Name'
            variant='outline'
            {...fields.lastName}
          />
          <StyledTextInputWithLabel
            label='Job Name'
            variant='outline'
            {...fields.jobName}
          />

          <StyledSelectWithLabel
            label='Manager Employee'
            menuPlacement='top'
            options={employeeOptions}
            {...fields.managerEmployeeId}
          />
        </FormSection>

        <FormSection label='Team'>
          <StyledSelectWithLabel
            label='Team'
            menuPlacement='top'
            options={teamOptions}
            {...fields.teamId}
          />
        </FormSection>

        <SubmitContainer>
          <Clickable
            disabled={!formState.isValid || !formState.isDirty}
            type='submit'
            variant='primary'
          >
            Add Employee
          </Clickable>
        </SubmitContainer>
      </FormContainer>
    </form>
  )
}

export default AddEmployeeForm

const StyledSelectWithLabel = styled(SelectWithLabel)`
  width: 100%;
`

const StyledMultiSelectWithLabel = styled(MultiSelectWithLabel)`
  width: 100%;
`

const StyledTextInputWithLabel = styled(TextInputWithLabel)`
  width: 100%;
  white-space: wrap;
`
